import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  ReactNode,
  useCallback,
} from "react";
import {
  getAllPublications,
  Publication,
  createPublication,
  updatePublication,
  deletePublication,
  updatePublicationOrder,
} from "../firebase/publicationService";
import { Timestamp } from "firebase/firestore";
import { withFirebaseRetry } from "../utils/retryUtils";
import { globalRateLimiter } from "../utils/rateLimiter";

// Cache duration in milliseconds (5 minutes)
const CACHE_DURATION = 5 * 60 * 1000;

interface PublicationContextProps {
  publications: Publication[];
  loading: boolean;
  error: string | null;
  refreshPublications: () => Promise<void>;
  addPublication: (
    publication: Omit<Publication, "id" | "createdAt" | "updatedAt">
  ) => Promise<string>;
  editPublication: (
    id: string,
    publication: Partial<Omit<Publication, "id" | "createdAt" | "updatedAt">>
  ) => Promise<void>;
  removePublication: (id: string) => Promise<void>;
  reorderPublications: (
    reorderedPublications: { id: string; order: number }[]
  ) => Promise<void>;
}

const PublicationContext = createContext<PublicationContextProps>({
  publications: [],
  loading: true,
  error: null,
  refreshPublications: async () => {},
  addPublication: async () => "",
  editPublication: async () => {},
  removePublication: async () => {},
  reorderPublications: async () => {},
});

export const usePublications = () => useContext(PublicationContext);

interface PublicationProviderProps {
  children: ReactNode;
}

export const PublicationProvider: React.FC<PublicationProviderProps> = ({
  children,
}) => {
  const [publications, setPublications] = useState<Publication[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [lastFetchTime, setLastFetchTime] = useState<number>(0);

  const fetchPublications = useCallback(
    async (forceRefresh: boolean = false) => {
      // Check if we have cached data and it's still fresh
      const now = Date.now();
      const isCacheValid =
        publications.length > 0 && now - lastFetchTime < CACHE_DURATION;

      if (!forceRefresh && isCacheValid) {
        console.log("Using cached publications data");
        return;
      }

      try {
        setLoading(true);
        setError(null);

        // Use retry mechanism for fetching publications
        const data = await withFirebaseRetry(async () => getAllPublications());

        setPublications(data);
        setLastFetchTime(now);
      } catch (err) {
        console.error("Error fetching publications:", err);
        setError("Failed to load publications. Please try again later.");
      } finally {
        setLoading(false);
      }
    },
    [publications.length, lastFetchTime]
  );

  // Fetch once on mount. Do NOT depend on `fetchPublications` — its useCallback deps
  // include `lastFetchTime`, which the fetch updates, causing an infinite re-fetch
  // loop when the collection is empty (the length-based cache guard never trips).
  useEffect(() => {
    fetchPublications();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const refreshPublications = async () => {
    await fetchPublications(true); // Force refresh
  };

  const addPublication = async (
    publication: Omit<Publication, "id" | "createdAt" | "updatedAt">
  ): Promise<string> => {
    // Rate limit the operation
    if (!globalRateLimiter.isAllowed("publication-add")) {
      throw new Error(
        "Rate limit exceeded for publication operations. Please try again later."
      );
    }

    const id = await withFirebaseRetry(async () =>
      createPublication(publication)
    );

    // Update state directly instead of full refresh
    const newPublication: Publication = {
      ...publication,
      id,
      createdAt: Timestamp.now(),
      updatedAt: Timestamp.now(),
    };

    setPublications((prev) => [...prev, newPublication]);

    return id;
  };

  const editPublication = async (
    id: string,
    publication: Partial<Omit<Publication, "id" | "createdAt" | "updatedAt">>
  ): Promise<void> => {
    // Rate limit the operation
    if (!globalRateLimiter.isAllowed("publication-edit")) {
      throw new Error(
        "Rate limit exceeded for publication operations. Please try again later."
      );
    }

    await withFirebaseRetry(async () => updatePublication(id, publication));

    // Update state directly instead of full refresh
    const updatedPublication = { ...publication, updatedAt: Timestamp.now() };

    setPublications((prev) =>
      prev.map((item) =>
        item.id === id ? { ...item, ...updatedPublication } : item
      )
    );
  };

  const removePublication = async (id: string): Promise<void> => {
    // Rate limit the operation
    if (!globalRateLimiter.isAllowed("publication-remove")) {
      throw new Error(
        "Rate limit exceeded for publication operations. Please try again later."
      );
    }

    await withFirebaseRetry(async () => deletePublication(id));

    // Update state directly instead of full refresh
    setPublications((prev) =>
      prev.filter((publication) => publication.id !== id)
    );
  };

  const reorderPublications = async (
    reorderedPublications: { id: string; order: number }[]
  ): Promise<void> => {
    // Rate limit the operation
    if (!globalRateLimiter.isAllowed("publication-reorder")) {
      throw new Error(
        "Rate limit exceeded for publication operations. Please try again later."
      );
    }

    await withFirebaseRetry(async () =>
      updatePublicationOrder(reorderedPublications)
    );

    // For reorder operations, we need to refresh to get the correct order
    await fetchPublications(true);
  };

  return (
    <PublicationContext.Provider
      value={{
        publications,
        loading,
        error,
        refreshPublications,
        addPublication,
        editPublication,
        removePublication,
        reorderPublications,
      }}
    >
      {children}
    </PublicationContext.Provider>
  );
};
